/**
 * Minimal fetch wrapper for the ServiceLedger API.
 *
 * - Always negotiates JSON.
 * - Sends the mandatory CSRF header on every mutating method (D48 / SEC-02).
 * - Turns an RFC 9457 `application/problem+json` body into {@link ApiError}; a raw `Response`
 *   never escapes this module, so callers branch on `errorCode`, never on English prose
 *   (D16: the API returns codes, the UI owns the text).
 */

/** Header every mutating endpoint requires (D48). */
export const CSRF_HEADER = 'X-ServiceLedger-Request';

/** Double-submit token (SEC-02): the non-HttpOnly cookie the API sets at login, echoed in a header. */
export const CSRF_TOKEN_HEADER = 'X-CSRF-Token';
export const CSRF_COOKIE = '__Host-sl_csrf';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Transversal error codes of the API contract (src/ServiceLedger.Hosting/Http/ProblemDetailsSetup.cs). */
export const ERROR_CODES = {
  validationFailed: 'validation_failed',
  notFound: 'not_found',
  forbidden: 'forbidden',
  unauthenticated: 'unauthenticated',
  rateLimitExceeded: 'rate_limit_exceeded',
  internalError: 'internal_error',
  serviceUnavailable: 'service_unavailable',
  badRequest: 'bad_request',
  invalidCredentials: 'invalid_credentials',
  sessionExpired: 'session_expired',
  tenantContextRequired: 'tenant_context_required',
  csrfValidationFailed: 'csrf_validation_failed',
  invalidOrExpiredToken: 'invalid_or_expired_token',
  hasDependentRecords: 'has_dependent_records',
  concurrentModification: 'concurrent_modification',
  invalidStateTransition: 'invalid_state_transition',
  tenantWithoutAdministrator: 'tenant_without_administrator',
  /** ISO-25.5, `AM-134` point 3: the grant behind the session closed; back to `/platform/support-access`. */
  supportAccessExpired: 'support_access_expired',
  /** ISO-25.7: an endpoint API tokens, users, invitations, roles or role permissions refuse in a support session. */
  forbiddenInSupportSession: 'forbidden_in_support_session',
  /** Synthesised client-side when the request never reached the API. */
  networkError: 'network_error',
} as const;

/** Shape of an RFC 9457 problem document as the API emits it. */
export interface ProblemDocument {
  type?: string;
  title?: string;
  status?: number;
  detail?: string | null;
  instance?: string;
  errorCode?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
  extensions?: Record<string, unknown>;
}

/** The only error this module throws. */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly correlationId: string | undefined;
  readonly title: string | undefined;
  readonly detail: string | undefined;
  readonly errors: Record<string, string[]>;
  readonly extensions: Record<string, unknown> | undefined;

  constructor(init: {
    status: number;
    errorCode: string;
    correlationId?: string | undefined;
    title?: string | undefined;
    detail?: string | undefined;
    errors?: Record<string, string[]> | undefined;
    extensions?: Record<string, unknown> | undefined;
  }) {
    // The message is a diagnostic string for logs; the UI renders translated copy instead.
    super(`${init.errorCode} (HTTP ${String(init.status)})`);
    this.name = 'ApiError';
    this.status = init.status;
    this.errorCode = init.errorCode;
    this.correlationId = init.correlationId;
    this.title = init.title;
    this.detail = init.detail;
    this.errors = init.errors ?? {};
    this.extensions = init.extensions;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Non-2xx statuses whose JSON body is still a valid payload - `/health/ready` answers 503
   * with the health report itself. A body typed `application/problem+json` is always an error,
   * whatever this list says.
   */
  acceptStatuses?: readonly number[];
}

/** Fallback code when the response carries no problem document. */
function codeForStatus(status: number): string {
  switch (status) {
    case 400:
      return ERROR_CODES.badRequest;
    case 401:
      return ERROR_CODES.unauthenticated;
    case 403:
      return ERROR_CODES.forbidden;
    case 404:
      return ERROR_CODES.notFound;
    case 422:
      return ERROR_CODES.validationFailed;
    case 429:
      return ERROR_CODES.rateLimitExceeded;
    case 503:
      return ERROR_CODES.serviceUnavailable;
    default:
      return status >= 500 ? ERROR_CODES.internalError : ERROR_CODES.badRequest;
  }
}

/** The CSRF cookie value, or undefined before login (or when cookies are unavailable). */
export function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(`${CSRF_COOKIE}=`)) {
      return part.slice(CSRF_COOKIE.length + 1);
    }
  }

  return undefined;
}

function isProblemDocument(value: unknown): value is ProblemDocument {
  return typeof value === 'object' && value !== null;
}

/** Reads the body once and maps it to an {@link ApiError}; never rethrows a parse failure. */
async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDocument = {};

  try {
    const parsed: unknown = await response.json();
    if (isProblemDocument(parsed)) {
      problem = parsed;
    }
  } catch {
    // A proxy or a crash can answer with HTML or an empty body: the status still tells the story.
  }

  return new ApiError({
    status: problem.status ?? response.status,
    errorCode: problem.errorCode ?? codeForStatus(response.status),
    correlationId: problem.correlationId,
    title: problem.title,
    detail: problem.detail ?? undefined,
    errors: problem.errors,
    extensions: problem.extensions,
  });
}

/** The name a `Content-Disposition: attachment; filename="…"` header offers, unquoted. */
function attachmentFileName(response: Response): string | undefined {
  const header = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/u.exec(header);
  return match?.[1];
}

/**
 * Downloads a file the API answers as a blob rather than as JSON (FR-327, AM-130 point 5): a CSV
 * export, read with the session's own cookie and saved under the name the server chose, via an
 * ephemeral `<a download>` the browser never shows. A non-2xx answer is still `application/problem+json`
 * (a `403`, a `422 report_too_large`), so it goes through the same {@link ApiError} every other
 * request does.
 *
 * @throws {ApiError} for any non-2xx response.
 */
export async function downloadFile(path: string): Promise<void> {
  const response = await fetch(path, { credentials: 'same-origin' });

  if (!response.ok) {
    throw await toApiError(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = attachmentFileName(response) ?? '';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Step 2 of the three-step upload of `07` §4.11 (decision (c), `AM-165`): the bytes themselves, by
 * `PUT` to the destination step 1 answered with. It cannot go through {@link apiFetch}, which
 * negotiates JSON and serialises its body - here the body is the file, streamed as it is, and the
 * `Content-Type` is the media type the registration declared and the server validated, never
 * whatever the browser guessed from the name.
 *
 * It is a mutating method, so it carries the two CSRF headers every other write does (D48, SEC-02),
 * and a refusal comes back as the same {@link ApiError}: a `413` for the size, a `400` when the
 * first bytes are not those of the declared type (`15` §4.1).
 *
 * @throws {ApiError} for any non-2xx response, and for a transport failure.
 */
export async function putBytes(path: string, content: Blob, contentType: string): Promise<void> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': contentType,
    [CSRF_HEADER]: '1',
  };
  const csrfToken = readCsrfToken();
  if (csrfToken !== undefined) {
    headers[CSRF_TOKEN_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method: 'PUT',
      headers,
      body: content,
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiError({
      status: 0,
      errorCode: ERROR_CODES.networkError,
      title: cause instanceof Error ? cause.message : undefined,
    });
  }

  if (!response.ok) {
    throw await toApiError(response);
  }
}

/**
 * Performs a request against the API and returns the parsed JSON body.
 *
 * @throws {ApiError} for any non-2xx response, and for a transport failure.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await apiFetchOnce<T>(path, options);
  } catch (error) {
    // A write refused for its double-submit token: reading the session hands the browser a fresh
    // one, and the write goes again once. Any other refusal, or a second one, travels as it came.
    if (error instanceof ApiError && error.errorCode === ERROR_CODES.csrfValidationFailed) {
      await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).catch(() => undefined);
      return apiFetchOnce<T>(path, options);
    }
    throw error;
  }
}

async function apiFetchOnce<T>(path: string, options: RequestOptions): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };

  if (MUTATING_METHODS.has(method)) {
    headers[CSRF_HEADER] = '1';
    const csrfToken = readCsrfToken();
    if (csrfToken !== undefined) {
      headers[CSRF_TOKEN_HEADER] = csrfToken;
    }
  }

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;

  try {
    response = await fetch(path, {
      method,
      headers,
      // D23: the session lives in an opaque HttpOnly cookie, so it must ride along.
      credentials: 'same-origin',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // A cancellation is not a failure: TanStack Query aborts the signal when it drops a query
    // (the session request right after the login navigates away), and wrapping it would raise a
    // `network_error` the person never caused. The abort travels as it came, so the caller
    // recognises it as such.
    if (
      options.signal?.aborted === true ||
      (cause instanceof Error && cause.name === 'AbortError')
    ) {
      throw cause;
    }

    throw new ApiError({
      status: 0,
      errorCode: ERROR_CODES.networkError,
      title: cause instanceof Error ? cause.message : undefined,
    });
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isProblem = contentType.includes('application/problem+json');
  const isAccepted = options.acceptStatuses?.includes(response.status) ?? false;

  if (!response.ok && (isProblem || !isAccepted)) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError({
      status: response.status,
      errorCode: ERROR_CODES.internalError,
      title: 'The response body was not valid JSON.',
    });
  }
}
